import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ApiError } from '../types/api.types';

interface ApiState {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
}

interface ApiStateContextType extends ApiState {
    setLoading: (loading: boolean) => void;
    setError: (error: ApiError | null) => void;
    setSuccess: (success: boolean) => void;
    resetState: () => void;
}

const initialState: ApiState = {
    loading: false,
    error: null,
    success: false,
};

const ApiStateContext = createContext<ApiStateContextType | undefined>(undefined);

export const ApiStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<ApiState>(initialState);

    const setLoading = (loading: boolean) => {
        setState(prev => ({ ...prev, loading }));
    };

    const setError = (error: ApiError | null) => {
        setState(prev => ({ ...prev, error, success: false }));
    };

    const setSuccess = (success: boolean) => {
        setState(prev => ({ ...prev, success, error: null }));
    };

    const resetState = () => {
        setState(initialState);
    };

    return (
        <ApiStateContext.Provider
            value={{
                ...state,
                setLoading,
                setError,
                setSuccess,
                resetState,
            }}
        >
            {children}
        </ApiStateContext.Provider>
    );
};

export const useApiState = () => {
    const context = useContext(ApiStateContext);
    if (context === undefined) {
        throw new Error('useApiState must be used within an ApiStateProvider');
    }
    return context;
}; 